import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  ref,
  onValue,
  update,
  onDisconnect,
  set,
  serverTimestamp,
} from "firebase/database";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp as firestoreServerTimestamp,
} from "firebase/firestore";
import { rtdb, db } from "../FirebaseConfig/firebase";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import EmojiPicker from "emoji-picker-react";

function ChatMessages() {
  const [selectedUser, setSelectedUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [message, setMessage] = useState("");
  const [userStatus, setUserStatus] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [chatList, setChatList] = useState([]);
  const [filteredChatList, setFilteredChatList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [onlineUsers, setOnlineUsers] = useState({});
  const [error, setError] = useState(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [isSending, setIsSending] = useState(false); // Prevent duplicate sends

  const messagesEndRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const messageInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = storedUser?.id || storedUser?._id;
  const currentUserName =
    storedUser?.name ||
    `${storedUser?.firstName} ${storedUser?.lastName}` ||
    storedUser?.email?.split("@")[0];
  const currentUserProfile = storedUser?.profilePic || "";

  const { id, name, avatar, online } = location.state || {};
  const passedUser = useMemo(
    () => (id ? { id, name, avatar, online: online || false } : null),
    [id, name, avatar, online],
  );

  const chatRoomId = selectedUser
    ? [currentUserId, selectedUser.id].sort().join("_")
    : null;

  const clearError = () => {
    setError(null);
    setShowErrorToast(false);
  };

  // Filter chat list based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredChatList(chatList);
    } else {
      const filtered = chatList.filter((chat) =>
        chat.otherUserName.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredChatList(filtered);
    }
  }, [chatList, searchQuery]);

  // REAL-TIME CHAT LIST WITH PERFECT SORTING
  useEffect(() => {
    if (!currentUserId) return;

    const chatsQuery = query(
      collection(db, "chats"),
      where("participants", "array-contains", currentUserId),
      orderBy("lastMessageTime", "desc"),
    );

    const unsubscribe = onSnapshot(
      chatsQuery,
      (querySnapshot) => {
        const chats = [];

        querySnapshot.forEach((doc) => {
          const chatData = doc.data();
          const otherParticipant = chatData.participants.find(
            (id) => id !== currentUserId,
          );

          const lastMsgTime = chatData.lastMessageTime;
          const createdTime = chatData.createdAt || { seconds: 0 };
          const sortTime = lastMsgTime
            ? (lastMsgTime.toMillis?.() ?? lastMsgTime.seconds * 1000)
            : (createdTime.toMillis?.() ?? createdTime.seconds * 1000);

          chats.push({
            id: doc.id,
            ...chatData,
            otherUserId: otherParticipant,
            otherUserName:
              chatData.participantNames?.[otherParticipant] || "Unknown User",
            otherUserAvatar:
              chatData.participantAvatars?.[otherParticipant] ||
              `${process.env.PUBLIC_URL}/assets/images/avatars/01.png`,
            sortTimestamp: sortTime,
          });
        });

        chats.sort((a, b) => (b.sortTimestamp || 0) - (a.sortTimestamp || 0));
        setChatList(chats);
        setFilteredChatList(chats);
      },
      (error) => {
        console.error("Realtime chat list error:", error);
        setError("Failed to sync chats in real-time.");
        setShowErrorToast(true);
      },
    );

    return () => unsubscribe();
  }, [currentUserId]);

  const getOrCreateChat = useCallback(
    async (otherUserId, otherUserName, otherUserAvatar) => {
      if (!currentUserId || !otherUserId) {
        throw new Error("Missing user IDs for chat creation");
      }

      const chatId = [currentUserId, otherUserId].sort().join("_");
      const chatDocRef = doc(db, "chats", chatId);

      const chatDocSnap = await getDoc(chatDocRef);
      if (chatDocSnap.exists()) return chatId;

      const chatRef = ref(rtdb, `chats/${chatId}/participants`);
      await set(chatRef, {
        [currentUserId]: true,
        [otherUserId]: true,
      });

      await setDoc(chatDocRef, {
        participants: [currentUserId, otherUserId],
        participantNames: {
          [currentUserId]: currentUserName,
          [otherUserId]: otherUserName,
        },
        participantAvatars: {
          [currentUserId]: currentUserProfile,
          [otherUserId]:
            otherUserAvatar ||
            `${process.env.PUBLIC_URL}/assets/images/avatars/01.png`,
        },
        lastMessage: "",
        lastMessageTime: firestoreServerTimestamp(),
        createdAt: firestoreServerTimestamp(),
        updatedAt: firestoreServerTimestamp(),
      });

      return chatId;
    },
    [currentUserId, currentUserName, currentUserProfile],
  );

  useEffect(() => {
    const initializeSelectedUser = async () => {
      if (passedUser && passedUser.id !== selectedUser?.id) {
        try {
          clearError();
          setSelectedUser(passedUser);
          await getOrCreateChat(
            passedUser.id,
            passedUser.name,
            passedUser.avatar,
          );
        } catch (error) {
          console.error("Failed to initialize chat:", error);
          setError("Failed to start chat. Please try again.");
          setShowErrorToast(true);
        }
      }
    };
    initializeSelectedUser();
  }, [passedUser, getOrCreateChat]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Online Status
  useEffect(() => {
    if (!currentUserId) return;

    const userStatusRef = ref(rtdb, `onlineStatus/${currentUserId}`);
    set(userStatusRef, {
      online: true,
      lastSeen: null,
      typing: false,
      name: currentUserName,
      email: storedUser?.email || "",
      userId: currentUserId,
      profilePic: currentUserProfile,
      lastOnline: null,
      updatedAt: Date.now(),
    });

    onDisconnect(userStatusRef).set({
      online: false,
      lastSeen: serverTimestamp(),
      typing: false,
      name: currentUserName,
      email: storedUser?.email || "",
      userId: currentUserId,
      profilePic: currentUserProfile,
      lastOnline: serverTimestamp(),
      updatedAt: Date.now(),
    });

    return () => {
      update(userStatusRef, {
        online: false,
        lastSeen: serverTimestamp(),
        lastOnline: serverTimestamp(),
      });
    };
  }, [currentUserId, currentUserName, currentUserProfile]);

  // Online Users Listener
  useEffect(() => {
    const onlineStatusRef = ref(rtdb, "onlineStatus");
    const unsubscribe = onValue(onlineStatusRef, (snapshot) => {
      setOnlineUsers(snapshot.val() || {});
    });
    return () => unsubscribe();
  }, []);

  // Selected User Status (FIXED: Safe lastSeen handling)
  useEffect(() => {
    if (!selectedUser) return;
    const statusRef = ref(rtdb, `onlineStatus/${selectedUser.id}`);
    const unsubscribe = onValue(statusRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.lastSeen) {
        // Handle Firebase Timestamp object
        if (
          typeof data.lastSeen === "object" &&
          data.lastSeen.seconds != null
        ) {
          data.lastSeen = new Date(data.lastSeen.seconds * 1000);
        }
        // Handle plain timestamp number
        else if (typeof data.lastSeen === "number") {
          data.lastSeen = new Date(data.lastSeen);
        }
        // Invalid → keep null
        else {
          data.lastSeen = null;
        }
      }
      setUserStatus(data);
    });
    return () => unsubscribe();
  }, [selectedUser?.id]);

  // Messages Listener
  useEffect(() => {
    if (!chatRoomId || !selectedUser) {
      setChats([]);
      return;
    }

    const messagesRef = ref(rtdb, `chats/${chatRoomId}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const messagesData = snapshot.val();
      if (messagesData) {
        const messagesList = Object.entries(messagesData)
          .map(([id, data]) => ({ id, ...data }))
          .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        setChats(messagesList);
      } else {
        setChats([]);
      }
    });

    return () => unsubscribe();
  }, [chatRoomId, selectedUser?.id]);

  // Scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [chats]);

  useEffect(() => {
    if (selectedUser && messagesEndRef.current) {
      setTimeout(scrollToBottom, 100);
    }
  }, [selectedUser]);

  useEffect(() => {
    setMessage("");
    setShowEmojiPicker(false);
    messageInputRef.current?.focus();
  }, [selectedUser?.id]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollTo({
      top: messagesEndRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isSending || !message.trim() || !selectedUser || !chatRoomId) return;

    setIsSending(true);

    try {
      setError(null);

      const timestamp = Date.now();
      const newMessageRef = ref(
        rtdb,
        `chats/${chatRoomId}/messages/${timestamp}_${currentUserId}`,
      );

      const messageData = {
        text: message.trim(),
        senderId: currentUserId,
        senderName: currentUserName,
        timestamp: timestamp,
        type: "text",
        read: false,
      };

      await set(newMessageRef, messageData);

      const chatDocRef = doc(db, "chats", chatRoomId);
      await setDoc(
        chatDocRef,
        {
          lastMessage: message.trim(),
          lastMessageTime: firestoreServerTimestamp(),
          updatedAt: firestoreServerTimestamp(),
        },
        { merge: true },
      );

      setMessage("");
      setShowEmojiPicker(false);
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message.");
      setShowErrorToast(true);
    } finally {
      setIsSending(false);
      update(ref(rtdb, `onlineStatus/${currentUserId}`), { typing: false });
      if (typingTimeout) clearTimeout(typingTimeout);
    }
  };

  const handleTyping = (isTyping) => {
    if (!currentUserId) return;
    const userStatusRef = ref(rtdb, `onlineStatus/${currentUserId}`);
    if (isTyping) {
      update(userStatusRef, { typing: true });
      if (typingTimeout) clearTimeout(typingTimeout);
      const timeout = setTimeout(() => {
        update(userStatusRef, { typing: false });
      }, 2000);
      setTypingTimeout(timeout);
    } else {
      update(userStatusRef, { typing: false });
      if (typingTimeout) clearTimeout(typingTimeout);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const addEmoji = (emojiObject) => {
    setMessage((prev) => prev + emojiObject.emoji);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const closeEmojiPicker = () => {
    setShowEmojiPicker(false);
    setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const handleUserSelect = async (user) => {
    try {
      clearError();
      setSelectedUser(user);
      await getOrCreateChat(user.id, user.name, user.avatar);
    } catch (error) {
      console.error("Failed to switch chat:", error);
      setError("Failed to open chat.");
      setShowErrorToast(true);
    }
  };

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <div
      className="d-flex overflow-hidden"
      style={{ backgroundColor: "#efeae2", height: "80vh" }}
    >
      {/* Sidebar */}
      <div
        className="d-flex flex-column bg-white border-end"
        style={{
          width: "30%",
          minWidth: "350px",
          maxWidth: "500px",
          marginTop: "90px",
        }}
      >
        <div className="p-1 border-bottom bg-light" style={{ height: "59px" }}>
          <div className="d-flex justify-content-between align-items-center h-100">
            <div className="d-flex align-items-center">
              {/* Back button for responsive screens */}
              <button
                className="btn btn-link p-1 me-2 back_btn"
                onClick={() => navigate("/")}
                style={{ minWidth: "auto" }}
              >
                <span className="material-icons fs-5 text-dark">
                  arrow_back
                </span>
              </button>
              <h6 className="mb-0 fw-bold text-dark d-flex align-items-center">
                Chats
              </h6>
            </div>
            <div className="d-flex">
              <button className="btn btn-link p-1 me-2">
                <i className="bi bi-plus-circle fs-6 text-success"></i>
              </button>
              <button className="btn btn-link p-1">
                <i className="bi bi-pencil fs-6"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="p-2 border-bottom" style={{ height: "56px" }}>
          <div className="input-group h-100 d-flex align-items-center rounded-pill border bg-white shadow-sm">
            <span className="input-group-text bg-transparent border-0 ps-3">
              <span
                className="material-icons text-muted"
                style={{ fontSize: "20px" }}
              >
                search
              </span>
            </span>
            <input
              type="text"
              className="form-control border-0 shadow-none rounded-pill"
              placeholder="Search or start new chat"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto">
          {filteredChatList.length === 0 ? (
            <div className="d-flex flex-column justify-content-center align-items-center p-5 text-center text-muted">
              <i className="bi bi-chat-dots fs-1 mb-3"></i>
              <p className="mb-0">No conversations yet</p>
              <small>Start a new chat!</small>
            </div>
          ) : (
            filteredChatList.map((chat) => {
              const isOnline = onlineUsers[chat.otherUserId]?.online || false;
              const isActive = selectedUser?.id === chat.otherUserId;

              return (
                <div
                  key={chat.id}
                  className={`p-3 border-bottom cursor-pointer ${isActive ? "bg-success-subtle" : ""}`}
                  onClick={() =>
                    handleUserSelect({
                      id: chat.otherUserId,
                      name: chat.otherUserName,
                      avatar: chat.otherUserAvatar,
                      online: isOnline,
                    })
                  }
                  style={isActive ? { backgroundColor: "#d1e7dd" } : {}}
                >
                  <div className="d-flex align-items-center">
                    <div className="position-relative me-3">
                      <img
                        src={chat.otherUserAvatar}
                        alt={chat.otherUserName}
                        className="rounded-circle"
                        style={{ width: "50px", height: "50px" }}
                      />
                      <div
                        className={`position-absolute bottom-0 end-0 rounded-circle border border-white ${isOnline ? "bg-success" : "bg-gray-300"}`}
                        style={{ width: "12px", height: "12px" }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6
                          className={`mb-1 fw-semibold ${isActive ? "text-success" : "text-dark"}`}
                        >
                          {chat.otherUserName}
                        </h6>
                        <small className="text-muted">
                          {moment(
                            chat.lastMessageTime?.toDate() ||
                              chat.createdAt?.toDate(),
                          ).format("h:mm A")}
                        </small>
                      </div>
                      <p className="mb-0 small text-muted text-truncate">
                        {chat.lastMessage && chat.lastMessage.length <= 30
                          ? `${chat.lastMessage.substring(0, 30)}`
                          : chat.lastMessage.length > 30
                            ? `"${chat.lastMessage.substring(0, 30)}..."`
                            : "No messages yet"}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div
        className="flex-grow-1 d-flex flex-column"
        style={{ backgroundColor: "#efeae2", marginTop: "90px" }}
      >
        {selectedUser ? (
          <>
            <div
              className="bg-white p-2 border-bottom d-flex align-items-center position-relative"
              style={{ height: "80px" }}
            >
              {/* Mobile back button → navigate home */}
              {/* <button
                className="btn btn-link p-0 me-3 back_btn"
                onClick={() => navigate("/")}
              >
                <span className="material-icons fs-4 text-dark">
                  arrow_back
                </span>
              </button> */}
              <img
                src={selectedUser.avatar}
                alt={selectedUser.name}
                className="rounded-circle me-3"
                style={{ width: "35px", height: "35px" }}
              />
              <div className="flex-grow-1 d-flex flex-column justify-content-center">
                <h6 className="mb-0 fw-bold text-dark fs-6">
                  {selectedUser.name}
                </h6>
                <small className="text-success">
                  {userStatus?.online
                    ? userStatus.typing
                      ? "typing..."
                      : "online"
                    : userStatus?.lastSeen
                      ? `last seen ${moment(userStatus.lastSeen).fromNow()}`
                      : "last seen recently"}
                </small>
              </div>
              <div className="d-flex">
                <button className="btn btn-link p-0 me-2">
                  <i className="bi bi-telephone fs-6 text-dark"></i>
                </button>
                <button className="btn btn-link p-0 me-2">
                  <i className="bi bi-camera-video fs-6 text-dark"></i>
                </button>
                <button className="btn btn-link p-0">
                  <i className="bi bi-three-dots-vertical fs-6 text-dark"></i>
                </button>
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto p-4" ref={messagesEndRef}>
              {chats.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
                  <i className="bi bi-chat-square-text fs-1 mb-3 opacity-50"></i>
                  <h6 className="mb-1">No messages yet</h6>
                  <p className="mb-0 opacity-75">Start a conversation!</p>
                </div>
              ) : (
                chats.map((chat, idx) => {
                  const timestamp = chat.timestamp
                    ? new Date(chat.timestamp)
                    : new Date();
                  const prevTimestamp =
                    idx > 0 ? new Date(chats[idx - 1].timestamp) : null;
                  const showDate =
                    idx === 0 ||
                    moment(timestamp).format("YYYY-MM-DD") !==
                      moment(prevTimestamp).format("YYYY-MM-DD");
                  const isOwnMessage = chat.senderId === currentUserId;

                  return (
                    <div
                      key={chat.id || idx}
                      className={`mb-3 ${isOwnMessage ? "text-end" : "text-start"}`}
                    >
                      {showDate && (
                        <div className="text-center mb-4">
                          <span className="px-3 py-1 bg-white rounded-pill shadow-sm small text-muted">
                            {moment(timestamp).format("MMM DD, YYYY")}
                          </span>
                        </div>
                      )}
                      <div
                        className={`d-inline-block p-3 shadow-sm ${
                          isOwnMessage
                            ? "bg-success text-white ms-2"
                            : "bg-white me-2"
                        }`}
                        style={{
                          maxWidth: "60%",
                          borderTopLeftRadius: isOwnMessage ? "20px" : "20px",
                          borderBottomLeftRadius: isOwnMessage ? "20px" : "0",
                          borderTopRightRadius: isOwnMessage ? "20px" : "20px",
                          borderBottomRightRadius: isOwnMessage ? "0" : "20px",
                        }}
                      >
                        <p className="mb-1 small lh-sm">{chat.text}</p>
                        <small
                          className={`text-${isOwnMessage ? "light" : "muted"}`}
                        >
                          {moment(timestamp).format("h:mm A")}
                          {isOwnMessage && (
                            <i className="bi bi-check2-all ms-1"></i>
                          )}
                        </small>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="bg-white p-3 border-top position-relative">
              <form
                onSubmit={handleSubmit}
                className="d-flex align-items-center"
              >
                <div className="position-relative me-2">
                  <button
                    type="button"
                    className="btn btn-link p-2"
                    onClick={toggleEmojiPicker}
                    ref={emojiButtonRef}
                    disabled={isSending}
                  >
                    <span className="material-icons fs-4 text-muted">
                      insert_emoticon
                    </span>
                  </button>
                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="position-absolute bottom-100 start-0 mb-2 z-3 bg-white rounded shadow-lg"
                      style={{ zIndex: 1050 }}
                    >
                      <EmojiPicker
                        onEmojiClick={addEmoji}
                        theme="light"
                        height={350}
                        width={320}
                      />
                      <div className="p-2 border-top text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-success rounded-pill px-3"
                          onClick={closeEmojiPicker}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-grow-1 me-2">
                  <input
                    ref={messageInputRef}
                    type="text"
                    className="form-control border shadow-none p-3 rounded-pill bg-light"
                    placeholder="Type a message..."
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      handleTyping(true);
                    }}
                    onBlur={() => handleTyping(false)}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                  />
                </div>

                <button
                  type="submit"
                  className={`btn rounded-pill px-3 py-2 d-flex align-items-center justify-content-center ${
                    message.trim() && !isSending
                      ? "btn-success text-white"
                      : "btn-outline-secondary"
                  }`}
                  disabled={!message.trim() || isSending}
                  style={{ minWidth: "45px", height: "45px" }}
                >
                  {isSending ? (
                    <small className="fw-semibold">Sending...</small>
                  ) : (
                    <span className="material-icons">send</span>
                  )}
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-muted">
            <i className="bi bi-chat-dots fs-1 mb-3 opacity-50"></i>
            <h5>Select a chat</h5>
            <p className="mb-4 opacity-75">
              Pick a conversation to start chatting
            </p>
            <button
              className="btn btn-outline-secondary"
              onClick={handleBackClick}
            >
              <i className="bi bi-arrow-left me-2"></i>Back
            </button>
          </div>
        )}
      </div>

      {/* Error Toast */}
      {showErrorToast && error && (
        <div
          className="toast-container position-fixed bottom-0 end-0 p-3"
          style={{ zIndex: 1090 }}
        >
          <div
            className="toast show align-items-center text-white bg-danger border-0"
            role="alert"
          >
            <div className="d-flex">
              <div className="toast-body">{error}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={clearError}
              ></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatMessages;
